"use client";

import { Box, Flex, Grid, Text } from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import { ScrollReveal } from "./ScrollReveal";
import { SectionLabel } from "./SectionLabel";

export function LandingRewards() {
  const t = useTranslations("landing.redesign.rewards");

  const steps = [
    {
      title: t("step1_title"),
      desc: t("step1_desc"),
      tag: t("step1_tag"),
      last: false,
    },
    {
      title: t("step2_title"),
      desc: t("step2_desc"),
      tag: t("step2_tag"),
      last: false,
    },
    {
      title: t("step3_title"),
      desc: t("step3_desc"),
      tag: t("step3_tag"),
      last: false,
    },
    {
      title: t("step4_title"),
      desc: t("step4_desc"),
      tag: t("step4_tag"),
      last: true,
    },
  ];

  return (
    <Box
      as="section"
      id="rewards-flow"
      aria-labelledby="landing-rewards-heading"
      bg="white"
      borderTop="1px solid"
      borderColor="blackAlpha.100"
      py={{ base: 16, md: 90 }}
    >
      <Box
        maxW="1100px"
        mx="auto"
        py={{ base: 16, md: 25 }}
        px={{ base: 4, md: 0 }}
      >
        <Grid
          templateColumns={{ base: "1fr", md: "1fr 1fr" }}
          gap={{ base: 10, md: 15 }}
          alignItems="start"
          mb={{ base: 12, md: 28 }}
        >
          <ScrollReveal>
            <Box>
              <SectionLabel>{t("section_label")}</SectionLabel>
              <Text
                as="h2"
                id="landing-rewards-heading"
                fontFamily="heading"
                fontSize={{ base: "28px", md: "clamp(28px, 3.5vw, 42px)" }}
                fontWeight="800"
                letterSpacing="-1px"
                color="neutral.900"
                lineHeight={1.1}
              >
                {t("title_line1")}
                <br />
                {t("title_line2")}
              </Text>
            </Box>
          </ScrollReveal>

          <ScrollReveal delay={0.15}>
            <Box>
              <Text
                textStyle="sm"
                color="neutral.500"
                lineHeight={1.7}
                fontWeight="300"
              >
                {t("body")}
              </Text>
              <Flex gap={3} mt={5} flexWrap="wrap">
                <Box
                  py={2.5}
                  px={4}
                  bg="primary.50"
                  borderRadius="10px"
                  textStyle="xs"
                  color="primary.700"
                  fontWeight="500"
                >
                  {t("badge_no_card")}
                </Box>
                <Box
                  py={2.5}
                  px={4}
                  bg="success.100"
                  borderRadius="10px"
                  textStyle="xs"
                  color="success.800"
                  fontWeight="500"
                >
                  {t("badge_earn")}
                </Box>
              </Flex>
            </Box>
          </ScrollReveal>
        </Grid>

        <ScrollReveal delay={0.1}>
          <Box position="relative">
            <Grid
              templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }}
              gap={{ base: 8, md: 0 }}
              position="relative"
              zIndex={1}
            >
              {steps.map((step, i) => (
                <Box
                  key={step.title}
                  textAlign="center"
                  px={3}
                  position="relative"
                  _before={
                    i === 0
                      ? undefined
                      : {
                          content: '""',
                          display: { base: "none", md: "block" },
                          position: "absolute",
                          top: "28px",
                          left: 0,
                          w: "calc(50% - 28px)",
                          h: "2px",
                          borderRadius: "full",
                          bg: "primary.600",
                        }
                  }
                  _after={
                    i === steps.length - 1
                      ? undefined
                      : {
                          content: '""',
                          display: { base: "none", md: "block" },
                          position: "absolute",
                          top: "28px",
                          right: 0,
                          w: "calc(50% - 28px)",
                          h: "2px",
                          borderRadius: "full",
                          bg: "primary.500",
                        }
                  }
                >
                  <Flex
                    w="56px"
                    h="56px"
                    rounded="full"
                    bg="white"
                    border="2px solid"
                    borderColor="primary.600"
                    align="center"
                    justify="center"
                    mx="auto"
                    mb={4}
                    fontFamily="syne"
                    textStyle="lg"
                    fontWeight="800"
                    color="primary.600"
                  >
                    {i + 1}
                  </Flex>
                  <Text
                    fontFamily="heading"
                    textStyle="sm"
                    fontWeight="700"
                    color="neutral.900"
                    mb={1.5}
                  >
                    {step.title}
                  </Text>
                  <Text
                    textStyle="xs"
                    color="neutral.500"
                    lineHeight={1.6}
                    mb={2}
                  >
                    {step.desc}
                  </Text>
                  <Text
                    as="span"
                    display="inline-block"
                    mt={2}
                    textStyle="xs"
                    fontWeight="600"
                    letterSpacing="0.06em"
                    px={2.5}
                    py={0.5}
                    borderRadius="full"
                    bg={step.last ? "success.100" : "primary.50"}
                    color={step.last ? "success.800" : "primary.700"}
                  >
                    {step.tag}
                  </Text>
                </Box>
              ))}
            </Grid>
          </Box>
        </ScrollReveal>
      </Box>
    </Box>
  );
}
